import {
  ContextualMenu,
  IDragOptions,
  makeStyles,
  Modal,
  Stack,
} from '@fluentui/react';
import { useEffect } from 'react';
import USER_RANK_CREATE from './UserRankCreate.graphql';
import USER_RANK_UPDATE from './UserRankUpdate.graphql';

import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  UserRankCreateInput,
  UserRankUpdateInput,
} from 'common/types/globalTypes';
import { FormProvider, useForm } from 'react-hook-form';
import { cutToast } from 'utility/toast';
import { BasicForm } from './BasicForm';
import { Footer } from './Footer';
import { Header } from './Header';
import { rankOptionType, UserRankValues } from '../types';
import USER_RANK_FIELDS from './UserRankFields.graphql';
import { validationSchema } from './validation';
import {
  UserRankCreate,
  UserRankCreateVariables,
} from './__generated__/UserRankCreate';
import {
  UserRankUpdate,
  UserRankUpdateVariables,
} from './__generated__/UserRankUpdate';
const USER_RANK_FRAGMENT_NAME = 'UserRankFields';

const useStyles = makeStyles(() => ({
  container: {
    width: 800,
    maxHeight: 600,
  },
}));

type RankOptionViewModalProps = {
  onClose: () => void;
  option?: rankOptionType;
  onCreate?: (createdId: string) => void;
};
export const RankOptionViewModal: React.FC<RankOptionViewModalProps> = ({
  onClose,
  option,
  onCreate,
}) => {
  const styles = useStyles();
  const [createUserRank, { loading: createUserRankLoading, data: createData }] =
    useMutation<UserRankCreate, UserRankCreateVariables>(USER_RANK_CREATE, {
      errorPolicy: 'all',
    });

  const [updateUserRank, { loading: updateUserRankLoading }] = useMutation<
    UserRankUpdate,
    UserRankUpdateVariables
  >(USER_RANK_UPDATE, { errorPolicy: 'all' });

  const DragOptions: IDragOptions = {
    moveMenuItemText: 'Move',
    closeMenuItemText: 'Close',
    menu: ContextualMenu,
    dragHandleSelector: '.ms-Modal-scrollableContent > div > div:first-child ',
  };

  const formMethods = useForm<UserRankValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });
  const { handleSubmit, reset, trigger } = { ...formMethods };

  const onHandleSubmit = async (values: UserRankValues) => {
    const { userRank } = { ...values };

    if (option) {
      const { errors } = await updateUserRank({
        variables: {
          input: {
            id: option.key,
            rowTimestamp: option.rowTimestamp,
            userRankPatch: {
              userRank,
            },
          } as UserRankUpdateInput,
        },
      });
      if (errors?.length) cutToast.error(errors[0].message);
      else {
        cutToast.success('User rank updated successfully');
        onClose();
      }
    }

    //create user rank
    else {
      const { errors, data } = await createUserRank({
        variables: {
          input: {
            userRank: {
              userRank: userRank,
            },
          } as UserRankCreateInput,
        },
        update(cache, { data }) {
          const newRank = data?.userRankCreate?.userRankType;
          if (!newRank) return;

          cache.modify({
            fields: {
              userRankTypes(existing = { nodes: [] }) {
                return {
                  ...existing,
                  nodes: [
                    cache.writeFragment({
                      data: newRank,
                      fragment: USER_RANK_FIELDS,
                      fragmentName: USER_RANK_FRAGMENT_NAME,
                    }),
                    ...existing.nodes,
                  ],
                };
              },
            },
          });
        },
      });
      if (errors?.length) cutToast.error(errors[0].message);
      else {
        onClose();
        cutToast.success('User rank created successfully');
        if (data?.userRankCreate?.userRankType?.id)
          onCreate?.(data?.userRankCreate?.userRankType?.id);
      }
    }
  };
  useEffect(() => {
    reset({
      userRank: option ? option.text : null,
    });

    trigger();
  }, [option, reset, trigger]);

  return (
    <Modal isOpen isBlocking dragOptions={DragOptions}>
      <Stack className={styles.container}>
        <FormProvider {...formMethods}>
          <Header
            onClose={onClose}
            title={option ? 'Update User Rank' : 'Create User Rank'}
            isEdit={!!option}
          />

          <BasicForm />

          <Footer
            loading={updateUserRankLoading || createUserRankLoading}
            onSubmit={handleSubmit(onHandleSubmit)}
            onCancel={onClose}
          />
        </FormProvider>
      </Stack>
    </Modal>
  );
};
